const scalar = (value) =>
  typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9._:-]{0,199}$/.test(value);
const id = (value) => typeof value === 'string' && /^[a-f\d]{24}$/.test(value);
const label = (value) =>
  typeof value === 'string' &&
  value === value.trim() &&
  value.length > 0 &&
  value.length <= 200 &&
  !Array.from(value).some(
    (character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127
  );
const reference = (value) =>
  typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9._:-]{2,119}$/.test(value);
export const emptyFundingSettings = (schemaVersion = 2) => ({
  liabilityAccountId: '',
  clearingAccounts: { wire: '', ach: '', card: '' },
  ...(schemaVersion === 3
    ? { version: 3, repCustomers: [] }
    : { qbCustomerId: '', legalBuyerName: '' }),
  productIncomeAccountId: '',
  shippingIncomeAccountId: '',
  approvalReference: '',
  confirmedNoDuplicateProcessorPosting: false,
});
export function fundingConfiguration(policy, schemaVersion = policy?.version === 3 ? 3 : 2) {
  return policy && policy.version === schemaVersion && [2, 3].includes(schemaVersion)
    ? {
        liabilityAccountId: policy.liabilityAccountId,
        clearingAccounts: { ...policy.clearingAccounts },
        ...(schemaVersion === 3
          ? { version: 3, repCustomers: policy.repCustomers.map((rep) => ({ ...rep })) }
          : { qbCustomerId: policy.qbCustomerId, legalBuyerName: policy.legalBuyerName }),
        productIncomeAccountId: policy.productIncomeAccountId,
        shippingIncomeAccountId: policy.shippingIncomeAccountId,
        approvalReference: policy.approvalReference,
        confirmedNoDuplicateProcessorPosting: policy.confirmedNoDuplicateProcessorPosting,
      }
    : emptyFundingSettings(schemaVersion);
}
const validRepCustomers = (reps) =>
  Array.isArray(reps) &&
  reps.length > 0 &&
  reps.length <= 25 &&
  reps.every(
    (rep) =>
      rep &&
      Object.keys(rep).sort().join(',') === 'actorId,expectedDisplayName,qbCustomerId' &&
      id(rep.actorId) &&
      scalar(rep.qbCustomerId) &&
      label(rep.expectedDisplayName)
  ) &&
  new Set(reps.map((rep) => rep.actorId)).size === reps.length &&
  new Set(reps.map((rep) => rep.qbCustomerId)).size === reps.length;
export function validFundingConfiguration(row) {
  return (
    row &&
    scalar(row.liabilityAccountId) &&
    row.clearingAccounts &&
    Object.keys(row.clearingAccounts).sort().join(',') === 'ach,card,wire' &&
    ['wire', 'ach', 'card'].every(
      (method) => row.clearingAccounts?.[method] === '' || scalar(row.clearingAccounts?.[method])
    ) &&
    Object.values(row.clearingAccounts).some(scalar) &&
    (row.version === 3
      ? Object.keys(row).sort().join(',') ===
          'approvalReference,clearingAccounts,confirmedNoDuplicateProcessorPosting,liabilityAccountId,productIncomeAccountId,repCustomers,shippingIncomeAccountId,version' &&
        validRepCustomers(row.repCustomers)
      : scalar(row.qbCustomerId) && label(row.legalBuyerName)) &&
    scalar(row.productIncomeAccountId) &&
    scalar(row.shippingIncomeAccountId) &&
    ![row.productIncomeAccountId, row.shippingIncomeAccountId].includes(row.liabilityAccountId) &&
    ![row.productIncomeAccountId, row.shippingIncomeAccountId].some((account) =>
      Object.values(row.clearingAccounts).includes(account)
    ) &&
    !Object.values(row.clearingAccounts).includes(row.liabilityAccountId) &&
    reference(row.approvalReference) &&
    (row.version === 3
      ? typeof row.confirmedNoDuplicateProcessorPosting === 'boolean'
      : row.confirmedNoDuplicateProcessorPosting === true)
  );
}
export function readFundingSettings(row) {
  const modern = row?.schemaVersion === 3;
  const legacy = row?.schemaVersion === 1 || (!modern && row?.legacyReadOnly === true);
  if (
    ![1, 2, 3].includes(row?.schemaVersion) ||
    !Number.isSafeInteger(row.revision) ||
    row.revision < 0 ||
    typeof row.configured !== 'boolean' ||
    row.configured !== Boolean(row.policy) ||
    (!row.configured && row.policy !== null) ||
    (row.schemaVersion === 2 && typeof row.legacyReadOnly !== 'boolean') ||
    (row.schemaVersion === 1 &&
      (!Array.isArray(row.repCandidates) ||
        row.repCandidates.some(
          (rep) =>
            !id(rep.actorId) ||
            !['firstName', 'lastName', 'username'].every((key) => typeof rep[key] === 'string')
        ))) ||
    (modern &&
      (typeof row.draft !== 'boolean' ||
        row.draft !==
          Boolean(
            row.policy?.version === 3 && row.policy.confirmedNoDuplicateProcessorPosting === false
          ) ||
        typeof row.legacyReadOnly !== 'boolean' ||
        !Array.isArray(row.repCandidates) ||
        row.repCandidates.some(
          (rep) => !id(rep?.actorId) || !label(rep.displayName) || typeof rep.email !== 'string'
        ) ||
        new Set(row.repCandidates.map((rep) => rep.actorId)).size !== row.repCandidates.length)) ||
    row.readiness?.ready !== false ||
    ![
      'not_configured',
      'disabled',
      'requires_verification',
      'processor_posting_verification_required',
    ].includes(row.readiness.code) ||
    (modern && row.draft && row.readiness.code !== 'processor_posting_verification_required') ||
    (row.policy &&
      ((!modern && row.policy.version !== (legacy ? 1 : 2)) ||
        (modern && ![1, 2, 3].includes(row.policy.version)) ||
        row.policy.revision !== row.revision ||
        row.policy.currency !== 'USD' ||
        row.policy.treatment !== 'prepaid_liability_journal' ||
        (row.policy.version >= 2 && !validFundingConfiguration(fundingConfiguration(row.policy)))))
  )
    throw new Error('Cash funding settings unavailable');
  return row;
}
const canonical = (value) =>
  Array.isArray(value)
    ? value.map(canonical)
    : value && typeof value === 'object'
      ? Object.fromEntries(
          Object.keys(value)
            .sort()
            .map((key) => [key, canonical(value[key])])
        )
      : value;
export const sameFundingConfiguration = (a, b) =>
  JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
